/**
 * WIP Aging — Phase polish.
 *
 * Derives per-job WIP balance + age from JE activity on account 1200.
 * Pure selector — no writes, no provider coupling.
 *
 * WIP age = days since the OLDEST open WIP debit on the job.
 * A job is "closed" (excluded from aging) when net WIP balance == $0.
 *
 * Buckets follow the same shape as AR aging so the UI can reuse styling.
 */
import type { JournalEntry } from "../glTypes";

const WIP_ACCOUNT = "1200";

const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a: string, b: string) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
};

export type WipAgingBucket = "0-30" | "31-60" | "61-90" | "91-120" | "120+";
export const WIP_AGING_BUCKETS: WipAgingBucket[] =
  ["0-30", "31-60", "61-90", "91-120", "120+"];

function bucketize(days: number): WipAgingBucket {
  if (days <= 30) return "0-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  if (days <= 120) return "91-120";
  return "120+";
}

export interface JobWipRow {
  jobId: string;
  /** Net WIP balance (positive = capitalized cost on the books). */
  balance: number;
  /** Total debits ever booked to WIP for this job (cost capitalized). */
  totalDebits: number;
  /** Total credits ever booked (WIP released to COGS or written off). */
  totalCredits: number;
  /** ISO date of the oldest still-open WIP debit. */
  oldestOpenDate?: string;
  ageDays: number;
  bucket: WipAgingBucket;
  lineCount: number;
}

export interface WipAgingSummary {
  asOf: string;
  totalOpenBalance: number;
  jobCount: number;
  oldestAgeDays: number;
  bucketTotals: Record<WipAgingBucket, number>;
  bucketJobCount: Record<WipAgingBucket, number>;
  /** Jobs aged > 90d — stale capitalization, candidates for release or write-off. */
  staleBalance: number;
}

export function getWipAgingRows(
  journalEntries: JournalEntry[],
  asOf: string = today(),
): JobWipRow[] {
  const byJob = new Map<string, {
    debits: Array<{ date: string; amount: number }>;
    credits: Array<{ date: string; amount: number }>;
  }>();

  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (l.accountId !== WIP_ACCOUNT || !l.jobId) continue;
      const slot = byJob.get(l.jobId) ?? { debits: [], credits: [] };
      if (l.side === "debit") slot.debits.push({ date: je.postingDate, amount: l.amount });
      else slot.credits.push({ date: je.postingDate, amount: l.amount });
      byJob.set(l.jobId, slot);
    }
  }

  const rows: JobWipRow[] = [];
  for (const [jobId, slot] of byJob) {
    const totalDebits = slot.debits.reduce((s, d) => s + d.amount, 0);
    const totalCredits = slot.credits.reduce((s, c) => s + c.amount, 0);
    const balance = totalDebits - totalCredits;
    if (Math.round(balance * 100) <= 0) continue; // closed or over-released

    // FIFO-apply credits to oldest debits to find the oldest "still open" debit.
    const sortedDebits = [...slot.debits].sort((a, b) => a.date.localeCompare(b.date));
    let creditPool = totalCredits;
    let oldestOpenDate: string | undefined;
    for (const d of sortedDebits) {
      if (creditPool >= d.amount) { creditPool -= d.amount; continue; }
      oldestOpenDate = d.date;
      break;
    }
    const ageDays = oldestOpenDate ? daysBetween(oldestOpenDate, asOf) : 0;
    rows.push({
      jobId,
      balance,
      totalDebits,
      totalCredits,
      oldestOpenDate,
      ageDays,
      bucket: bucketize(ageDays),
      lineCount: slot.debits.length + slot.credits.length,
    });
  }
  return rows.sort((a, b) => b.ageDays - a.ageDays);
}

export function getWipAgingSummary(
  journalEntries: JournalEntry[],
  asOf: string = today(),
): WipAgingSummary {
  const rows = getWipAgingRows(journalEntries, asOf);
  const bucketTotals: Record<WipAgingBucket, number> =
    { "0-30": 0, "31-60": 0, "61-90": 0, "91-120": 0, "120+": 0 };
  const bucketJobCount: Record<WipAgingBucket, number> =
    { "0-30": 0, "31-60": 0, "61-90": 0, "91-120": 0, "120+": 0 };
  let oldest = 0;
  let stale = 0;
  for (const r of rows) {
    bucketTotals[r.bucket] += r.balance;
    bucketJobCount[r.bucket] += 1;
    oldest = Math.max(oldest, r.ageDays);
    if (r.ageDays > 90) stale += r.balance;
  }
  return {
    asOf,
    totalOpenBalance: rows.reduce((s, r) => s + r.balance, 0),
    jobCount: rows.length,
    oldestAgeDays: oldest,
    bucketTotals,
    bucketJobCount,
    staleBalance: stale,
  };
}
